import { useBoundStore } from '../../store/store'
import type {
  AmountInputProps,
  DepositBonusTier,
} from '../../types/wallet-management'
import classNames from '../../utils/classNames'
import { getAmountBonusMarker } from '../../utils/deposit-bonus'
import { numberWithCommas } from '../../utils/number'

interface AmountSelectorProps
  extends Pick<AmountInputProps, 'amount' | 'setAmount'> {
  amounts: number[]
  badges?: string[]
  // Per-amount bonuses from the channel, matched to a chip by amount.
  tiers?: DepositBonusTier[]
  // Whether the per-amount bonuses are on offer at all. With bonuses switched
  // off the chips carry no markers.
  showBonuses?: boolean
  // The chip the player picked, or null. Tracked by the modal rather than
  // derived from `amount`, because picking a promotion clears the chip's
  // selection while leaving its value in the amount field.
  selectedAmount?: number | null
  // Called with the picked amount, so the modal can mark it selected and drop
  // any promotion the player had chosen instead.
  onSelectAmount?: (amount: number) => void
}

const AmountBadge = ({
  label,
  lowered,
}: {
  label?: string
  lowered?: boolean
}) => {
  if (!label) return null
  return (
    <span
      className={classNames(
        'absolute -right-1 z-10 flex items-center',
        // Dropped below the bonus ribbon on a chip carrying both — with bonuses
        // on, a promotion can be selected while the ribbons are still drawn.
        lowered ? 'top-3.5' : '-top-1',
      )}>
      <span
        className="h-0 w-0"
        style={{
          borderTop: '7px solid transparent',
          borderBottom: '7px solid transparent',
          borderRight: '4px solid #E91E63',
        }}
      />
      <span className="bg-[#E91E63] text-white text-[8px] font-bold px-1 py-[1px] rounded-r-sm xl:text-[10px]">
        {label}
      </span>
    </span>
  )
}

// The percentage bonus, as a green ribbon in the chip's top-right corner. Green
// in both themes, like the "recommended" channel badge it visually matches.
// Takes a formatted label so the currency-vs-percent wording stays with the
// caller that knows which kind it is.
const BonusBadge = ({ label }: { label: string }) => (
  <span className="green-metallic absolute -top-1 -right-1 z-10 text-white text-[11px] font-bold leading-none px-1.5 py-[3px] rounded-sm xl:text-[13px]">
    {label}
  </span>
)

const AmountSelector = ({
  amount,
  setAmount,
  amounts,
  badges,
  tiers,
  showBonuses = true,
  selectedAmount,
  onSelectAmount,
}: AmountSelectorProps) => {
  const theme = useBoundStore(state => state.common.theme)
  // The flat amount takes the themed accent: blue in light, gold in dark — the
  // same pair the active chip's border and label use.
  const bonusAccent =
    theme === 'light' ? 'text-highlight' : 'text-highlight-gold'
  return (
    <div className="grid grid-cols-3 gap-2">
      {amounts.map((item, index) => {
        // Falls back to matching the field for call sites that don't track a
        // selection of their own.
        const isSelected =
          selectedAmount === undefined
            ? +amount.replaceAll(',', '') === item
            : selectedAmount === item
        const marker = showBonuses ? getAmountBonusMarker(tiers, item) : null

        return (
          <button
            key={index}
            onClick={() => {
              setAmount(numberWithCommas(item))
              onSelectAmount?.(item)
            }}
            className={classNames(
              'relative text-sm py-2 rounded-md border border-solid transition-all duration-200 xl:text-base xl:py-3 dark:bg-[#181818] dark:border-[rgba(255,255,255,0.10)] dark:rounded-lg',
              isSelected
                ? 'deposit-chip-active border-highlight text-highlight dark:!border-[#f5c542] dark:!text-[#f5c542] dark:shadow-[0_0_14px_rgba(245,197,66,0.13)]'
                : 'border-secondary-txt-20 text-txt',
            )}
            style={
              isSelected && theme === 'light'
                ? {
                    background:
                      'linear-gradient(to bottom, color-mix(in srgb, var(--highlight) 5%, transparent))',
                  }
                : undefined
            }>
            {marker?.kind === 'percent' && (
              <BonusBadge label={`+${marker.bonus}%`} />
            )}
            <AmountBadge
              label={badges?.[index]}
              lowered={marker?.kind === 'percent'}
            />
            <span className="block">${numberWithCommas(item)}</span>
            {marker?.kind === 'flat' && (
              <span
                className={classNames(
                  'block font-semibold text-[10px] leading-none mt-0.5 xl:text-xs',
                  bonusAccent,
                )}>
                +${numberWithCommas(marker.bonus)}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

export default AmountSelector
